import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';
import {
  ContainerBox,
  FormCard,
  FormContentContainer,
} from '../../../core/components/styled/StyledComponents';
import {
  ENROLLMENT_FORM_STEPS,
  EnrollmentStatus,
  EnrollmentSteps,
  UserStatus,
} from '../../../core/constant/constant';
import { ErrorSource } from '../../../core/constant/model/api/api';
import ApiErrorLabel from '../../../core/components/ui/ApiErrorLabel';
import { useAuth } from '../../auth/hooks/useAuth';
import { stepConfig } from '../utils/enrollmentStepConfig';
import { EnrollmentFormData } from '../types/enrollment';
import { useDispatch, useSelector } from 'react-redux';
import { clearApiError } from '../../../core/store/slices/apiError.slice';

import FormSidebar from './FormSidebar';
import MobileHeader from './MobileHeader';
import FormNavigation from './FormNavigation';
import { useEnrollmentForm, useApiErrors, useEnrollmentSubmission } from '../hooks/enrollmentHooks';
import StepContent from './steps/StepContent';
import { RootState } from '../../../core/store';
import { setEnrollmentStatus } from '../../../core/store/slices/enrollment.slice';
import { useProfileQuery } from '../../auth/hooks/useProfileQuery';
import { useCompleteEnrollmentMutation } from '../hooks/enrollmentApi';
import moment from 'moment';
const MindRightEnrollment: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userId } = useAuth();
  const formContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const enrollmentStep = useSelector((state: RootState) => state.enrollment.enrollmentStep);
  const { activeStep, setActiveStep, isSaving, setIsSaving, methods, getProgressValue } =
    useEnrollmentForm();
  const memberSubscription = useSelector((state: RootState) => state.memberSubscription);
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const isMemberSubscriptionActive =
    memberSubscription?.status === EnrollmentStatus.MEMBERSHIP_ACTIVE &&
    memberSubscription.firstPaymentId;
  const { hasApiError, getApiErrorBySource } = useApiErrors();
  const { submitStepData } = useEnrollmentSubmission(userId);
  const [isForceStep, setIsForceStep] = useState(0);
  const profile = useProfileQuery();
  const completeEnrollmentMutation = useCompleteEnrollmentMutation();

  // Check if user is ACTIVE_COACHING or COMMUNITY_MEMBER with incomplete enrollment
  const isSpecialStatusWithIncompleteEnrollment =
    (basicInfo.currentStatus === UserStatus.ACTIVE_COACHING ||
     basicInfo.currentStatus === UserStatus.COMMUNITY_MEMBER) &&
    !basicInfo.userEnrollment?.completed;
  // const {
  //   isBlocked,
  //   setIsPartnerNoneSaved,
  //   blockReasonMessage
  // } = usePartnerBlockingStatus(activeStep, primaryPartnerName);
  const handleStepChange = useCallback(() => {
    if (enrollmentStep === EnrollmentSteps.ENROLLMENT_CONTACT_INFO || isForceStep === 0) {
      setActiveStep(0);
    }
    if (enrollmentStep === EnrollmentSteps.ENROLLMENT_PERSONAL_DETAILS || isForceStep === 1) {
      setActiveStep(1);
    }
    if (enrollmentStep === EnrollmentSteps.ENROLLMENT_COMPLETE || isForceStep === 2) {
      setActiveStep(2);
    }
    profile.refetch();
  }, [setActiveStep, profile, isForceStep]);

  useEffect(() => {
    methods.clearErrors();
    methods.setValue('stepKey', ENROLLMENT_FORM_STEPS[activeStep]?.key);
    handleStepChange();
  }, [activeStep, methods, enrollmentStep, isForceStep]);

  const enrollmentUpdateError = hasApiError(ErrorSource.UPDATE_ENROLLMENT)
    ? getApiErrorBySource(ErrorSource.UPDATE_ENROLLMENT)
    : null;

  const completeEnrollmentError = hasApiError(ErrorSource.COMPLETE_ENROLLMENT)
    ? getApiErrorBySource(ErrorSource.COMPLETE_ENROLLMENT)
    : null;

  const handleDismissError = useCallback(() => {
    dispatch(clearApiError(ErrorSource.UPDATE_ENROLLMENT));
    dispatch(clearApiError(ErrorSource.COMPLETE_ENROLLMENT));
  }, [dispatch]);

  const handleBack = useCallback((): void => {
    setActiveStep((prevStep: number) => {
      dispatch(setEnrollmentStatus(ENROLLMENT_FORM_STEPS[prevStep - 1]?.key));
      return Math.max(0, prevStep - 1);
    });
    setIsForceStep((prev) => Math.max(0, prev - 1));
    formContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [setActiveStep]);

  const handleEditSection = useCallback(
    (stepIndex: number): void => {
      setActiveStep(() => {
        dispatch(setEnrollmentStatus(ENROLLMENT_FORM_STEPS[stepIndex]?.key));
        return stepIndex;
      });
      setIsForceStep(stepIndex);
      formContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
    [setActiveStep]
  );

  const handleFinalSubmit = useCallback(async (): Promise<void> => {
    // COMMUNITY_MEMBER or SIGNED_UP: go to subscriptions page to go through full enrollment
    // This is regardless of subscription status or enrollment completion
    if (basicInfo.currentStatus === UserStatus.COMMUNITY_MEMBER ||
        basicInfo.currentStatus === UserStatus.SIGNED_UP) {
      navigate('/subscriptions?isNewEnrollment=true');
      dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_PAYMENT));
      return;
    }

    // Special handling for ACTIVE_COACHING with incomplete enrollment
    // These users should skip additional info steps and go directly to application-received page
    if (isSpecialStatusWithIncompleteEnrollment && isMemberSubscriptionActive) {
      try {
        // Call complete enrollment API to update status to PENDING MATCHING
        await completeEnrollmentMutation.mutateAsync({
          memberId: userId,
          payload: { currentNewStatus: UserStatus.PENDING_MATCHING }
        });
        await profile.refetch();
        navigate('/application-received', { replace: true });
        dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_COMPLETE));
      } catch (error) {
        console.error('Error completing enrollment:', error);
        // Even if API fails, still navigate to application-received
        await profile.refetch();
        navigate('/application-received', { replace: true });
        dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_COMPLETE));
      }
    } else if (isMemberSubscriptionActive) {
      navigate('/addition-information', { replace: true });
      dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_PREFERENCE_INFORMATION));
    } else {
      navigate('/subscriptions?isNewEnrollment=true');
      dispatch(setEnrollmentStatus(EnrollmentSteps.ENROLLMENT_PAYMENT));
    }
  }, [navigate, isMemberSubscriptionActive, isSpecialStatusWithIncompleteEnrollment, profile, dispatch, completeEnrollmentMutation, userId, basicInfo.currentStatus]);

  const handleNext = useCallback(async (): Promise<void> => {
    try {
      const isValid = await methods.trigger();

      if (!isValid) {
        console.log('Validation errors:', methods.formState.errors);
        return;
      }

      const formData: EnrollmentFormData = methods.getValues();
      const currentConfig = stepConfig[activeStep];

      if (!currentConfig) return;

      setIsSaving(true);
      if (activeStep === ENROLLMENT_FORM_STEPS.length - 1) {
        await handleFinalSubmit();
        return;
      }

      if (!formData.isStudent) {
        delete formData.school;
      }
      delete formData.isStudent;
      const result = await submitStepData(
        activeStep,
        formData,
        currentConfig.updateRedux,
        currentConfig.prepareApiData
      );
      if (result.success) {
        setActiveStep((prevStep) => {
          dispatch(setEnrollmentStatus(ENROLLMENT_FORM_STEPS[prevStep + 1]?.key));
          return prevStep + 1;
        });
        setIsForceStep((prev) => prev + 1);
        formContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error saving step data:', error);
    } finally {
      setIsSaving(false);
    }
  }, [
    activeStep,
    methods,
    submitStepData,
    setIsSaving,
    setActiveStep,
    handleFinalSubmit,
    setEnrollmentStatus,
  ]);

  useEffect(() => {
    if (Object.keys(methods.formState.errors).length > 0) {
      setTimeout(() => {
        const container = formContainerRef.current;
        if (!container) return;

        const errorElements = container.getElementsByClassName('Mui-error');

        if (errorElements.length > 0) {
          const firstError = errorElements[0] as HTMLElement;
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // firstError.focus?.();
        }
      }, 0);
    }
  }, [methods.formState.errors]);

  return (
    <ContainerBox sx={{ padding: '0px' }}>
      <FormCard>
        <Box sx={{ display: 'flex', width: '100%' }}>
          <FormProvider {...methods}>
            <FormSidebar
              activeStep={activeStep}
              getProgressValue={getProgressValue}
              options={ENROLLMENT_FORM_STEPS}
              title='Ready to start?'
              description='Complete your profile'
            />

            <FormContentContainer ref={formContainerRef}>
              {isMobile && (
                <MobileHeader activeStep={activeStep} getProgressValue={getProgressValue} />
              )}
              <form id='enrollment-form' onSubmit={(e) => e.preventDefault()}>
                <StepContent
                  activeStep={activeStep}
                  isMobile={isMobile}
                  isSaving={isSaving}
                  onEditSection={handleEditSection}
                />
              </form>

              <FormNavigation
                activeStep={activeStep}
                handleBack={handleBack}
                handleNext={handleNext}
                isSaving={isSaving}
              />

              {enrollmentUpdateError && (
                <ApiErrorLabel
                  message={enrollmentUpdateError.message}
                  variant='alert'
                  testId='api-error'
                  onDismiss={handleDismissError}
                  dismissible={true}
                />
              )}
              {completeEnrollmentError && (
                <ApiErrorLabel
                  message={completeEnrollmentError.message}
                  variant='alert'
                  testId='api-error'
                  onDismiss={handleDismissError}
                  dismissible={true}
                />
              )}
            </FormContentContainer>
          </FormProvider>
        </Box>
      </FormCard>
    </ContainerBox>
  );
};

export default React.memo(MindRightEnrollment);
